import React, { useEffect, useState } from 'react';
import api from '../../api/client';
import { XCircle, TrendingUp, User } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, Legend } from 'recharts';

const COLORS = ['#2575fc', '#6a11cb', '#e67e22', '#2ecc71', '#ff4d4f', '#faad14', '#3498db', '#1abc9c'];

const RejectionAnalysis: React.FC = () => {
  const [reasonsCloud, setReasonsCloud] = useState<{ name: string; count: number }[]>([]);
  const [weeklyTrends, setWeeklyTrends] = useState<any[]>([]);
  const [recruiterBreakdown, setRecruiterBreakdown] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await api.get('/recruitment-analytics/rejection-analysis');
        setReasonsCloud(res.data.reasonsCloud || []);
        setWeeklyTrends(res.data.weeklyTrends || []);
        setRecruiterBreakdown(res.data.recruiterBreakdown || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) return <div style={{ padding: 40 }}>Загрузка...</div>;

  const topCategories = reasonsCloud.map(r => r.name).slice(0, 5);

  return (
    <div style={{ padding: '0 20px 20px 20px' }}>
      <h2 style={styles.title}><XCircle size={24} /> Анализ причин отказа</h2>

      <div style={styles.card}>
        <h3 style={styles.chartTitle}>Топ-10 причин отказов</h3>
        {reasonsCloud.length === 0 ? <p style={styles.empty}>Нет данных</p> : (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={reasonsCloud}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" fill="rgba(231,76,60,0.8)" radius={[8, 8, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={styles.chartTitle}><TrendingUp size={16} /> Динамика по неделям</h3>
        {weeklyTrends.length === 0 ? <p style={styles.empty}>Нет данных</p> : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={weeklyTrends}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="week" />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              {topCategories.map((cat, i) => (
                <Line key={cat} type="monotone" dataKey={cat} stroke={COLORS[i % COLORS.length]} strokeWidth={2} dot={false} />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={styles.chartTitle}><User size={16} /> Отказы по рекрутерам</h3>
        {recruiterBreakdown.length === 0 ? <p style={styles.empty}>Нет данных</p> : (
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Рекрутер</th>
                <th style={styles.th}>Всего</th>
                <th style={styles.th}>Основные причины</th>
              </tr>
            </thead>
            <tbody>
              {recruiterBreakdown.map(r => {
                const top = Object.entries(r.reasons).sort(([,a], [,b]) => (b as number) - (a as number)).slice(0, 3).map(([n]) => n).join(', ');
                return (
                  <tr key={r.recruiterId}>
                    <td style={styles.td}>{r.name}</td>
                    <td style={styles.td}>{r.total}</td>
                    <td style={styles.td}>{top || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  title: { fontSize: 24, fontWeight: 700, marginBottom: 24, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 },
  card: {
    background: 'rgba(255,255,255,0.55)', backdropFilter: 'blur(20px)',
borderRadius: 24,
    padding: 20,
    marginBottom: 20,
    boxShadow: '0 12px 40px rgba(31,38,135,0.06)',
    border: '1px solid rgba(255,255,255,0.6)'
  },
  chartTitle: { fontSize: 18, fontWeight: 600, marginBottom: 16, color: '#1a1a2e', display: 'flex', alignItems: 'center', gap: 8 },
  empty: { textAlign: 'center', padding: 20, color: '#999' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 14 },
  th: { textAlign: 'left', padding: '12px 16px', background: 'rgba(37,117,252,0.05)', borderBottom: '2px solid rgba(37,117,252,0.2)', color: '#2575fc', fontWeight: 600 },
  td: { padding: '10px 16px', borderBottom: '1px solid rgba(0,0,0,0.05)' }
};

export default RejectionAnalysis;